import { errorResponseSchema, type ErrorCode, type RouteContract } from '@spot/shared';
import type { z } from 'zod';

/** Shared API error codes plus the two the client produces itself. */
export type ApiErrorCode = ErrorCode | 'network_error' | 'invalid_response';

export class ApiError extends Error {
	constructor(
		readonly code: ApiErrorCode,
		readonly status: number,
		message: string,
		readonly details?: unknown,
	) {
		super(message);
		this.name = 'ApiError';
	}
}

export interface ApiAuth {
	getToken(): string | null;
	/** Gets a fresh token (repeats the Telegram auth). Called at most once per request. */
	refresh(): Promise<string>;
}

// `unknown` is the identity for `&`, so absent parts simply disappear from the intersection.
type Part<S, K extends string> = S extends z.ZodType ? Record<K, z.input<S>> : unknown;

/** `{ body?, query?, params? }`, each present only if the contract defines it. */
export type CallInput<C extends RouteContract> = Part<C['body'], 'body'> &
	Part<C['query'], 'query'> &
	Part<C['params'], 'params'>;

type CallArgs<C extends RouteContract> = keyof CallInput<C> extends never ? [] : [CallInput<C>];

export interface ApiClient {
	call<C extends RouteContract>(
		contract: C,
		...args: CallArgs<C>
	): Promise<z.output<C['response']>>;
	/**
	 * Sends a binary body (e.g. a photo, `PUT /v1/me/photo`) with auth and one re-login on 401.
	 * The response is validated with `response`.
	 */
	upload<S extends z.ZodType>(
		path: string,
		body: Blob,
		response: S,
		method?: 'PUT' | 'POST',
	): Promise<z.output<S>>;
}

/** Fills `:name` path segments and appends the query string (undefined values are skipped). */
export function buildPath(
	path: string,
	params: Record<string, unknown> | undefined,
	query: Record<string, unknown> | undefined,
): string {
	const filled = path.replace(/:([A-Za-z]+)/g, (_match, name: string) => {
		const value = params?.[name];
		if (typeof value !== 'string' && typeof value !== 'number') {
			throw new Error(`Missing path param "${name}"`);
		}
		return encodeURIComponent(String(value));
	});
	const search = new URLSearchParams();
	for (const [key, value] of Object.entries(query ?? {})) {
		if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
			search.set(key, String(value));
		}
	}
	const qs = search.toString();
	return qs === '' ? filled : `${filled}?${qs}`;
}

/** Typed fetch client over the shared route contracts (D28). Responses are validated with Zod. */
export function createApiClient(options: {
	baseUrl: string;
	auth: ApiAuth;
	fetch?: typeof fetch;
}): ApiClient {
	const doFetch = options.fetch ?? globalThis.fetch.bind(globalThis);

	async function send(contract: RouteContract, url: string, body: unknown, token: string | null) {
		const headers: Record<string, string> = { accept: 'application/json' };
		if (body !== undefined) headers['content-type'] = 'application/json';
		if (contract.auth && token !== null) headers.authorization = `Bearer ${token}`;
		return request(url, {
			method: contract.method,
			headers,
			...(body === undefined ? {} : { body: JSON.stringify(body) }),
		});
	}

	async function request(url: string, init: RequestInit) {
		try {
			return await doFetch(`${options.baseUrl}${url}`, init);
		} catch {
			throw new ApiError('network_error', 0, 'Network request failed');
		}
	}

	async function upload<S extends z.ZodType>(
		path: string,
		body: Blob,
		responseSchema: S,
		method: 'PUT' | 'POST' = 'PUT',
	): Promise<z.output<S>> {
		const attempt = (token: string | null) =>
			request(path, {
				method,
				headers: {
					accept: 'application/json',
					'content-type': body.type,
					...(token === null ? {} : { authorization: `Bearer ${token}` }),
				},
				body,
			});
		let response = await attempt(options.auth.getToken());
		if (response.status === 401) response = await attempt(await options.auth.refresh());
		if (!response.ok) throw await toError(response);
		const json: unknown = await response.json().catch(() => undefined);
		const parsed = responseSchema.safeParse(json);
		if (!parsed.success) {
			throw new ApiError('invalid_response', response.status, 'Unexpected response shape');
		}
		return parsed.data;
	}

	async function toError(response: Response): Promise<ApiError> {
		const json: unknown = await response.json().catch(() => null);
		const parsed = errorResponseSchema.safeParse(json);
		if (!parsed.success) {
			return new ApiError('invalid_response', response.status, `HTTP ${response.status}`);
		}
		const { code, message, details } = parsed.data.error;
		return new ApiError(code, response.status, message, details);
	}

	async function call<C extends RouteContract>(
		contract: C,
		...args: CallArgs<C>
	): Promise<z.output<C['response']>> {
		const input = (args[0] ?? {}) as { body?: unknown; query?: unknown; params?: unknown };
		// Validate everything before sending; the raw input (not the parsed output) is serialized,
		// so transforms in the shared schemas run on the server side only.
		const body = contract.body === undefined ? undefined : contract.body.parse(input.body);
		contract.query?.parse(input.query);
		contract.params?.parse(input.params);
		const url = buildPath(
			contract.path,
			input.params as Record<string, unknown> | undefined,
			input.query as Record<string, unknown> | undefined,
		);

		let response = await send(contract, url, body, options.auth.getToken());
		if (response.status === 401 && contract.auth) {
			// Expired/invalid token: re-authenticate once and retry the same request.
			response = await send(contract, url, body, await options.auth.refresh());
		}
		if (!response.ok) throw await toError(response);

		const json: unknown = await response.json().catch(() => undefined);
		const parsed = contract.response.safeParse(json);
		if (!parsed.success) {
			throw new ApiError('invalid_response', response.status, 'Unexpected response shape');
		}
		// Validated by `contract.response` above; the generic only loses the link to C['response'].
		return parsed.data as z.output<C['response']>;
	}

	return { call, upload };
}
